import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import {
  assessmentAnswerMapSchema,
  assessmentExtensionSchema,
  assessmentResultKeySchema,
  assessmentSessionStatusSchema,
} from '../api/assessments';
import {
  normalisedOnboardingAnswerSchema,
  onboardingAnswersSchema,
  onboardingStatusSchema,
} from '../onboarding-sessions/schema';
import { aiConsentStatusSchema } from '../consents/sona-consent';
import { intakeBaselineSchema } from '../baselines/schema';

// Staff-facing read contract for a member's intake record — the onboarding
// answers plus the adaptive PHQ/GAD screening — served in one body.
//
// This is a PROJECTION across three tables (onboarding_sessions,
// assessment_sessions, assessment_answers) plus the member's latest Sona
// consent row, with wire coercions (timestamptz → ISO string, text+CHECK →
// z.enum), so per ADR-066 Rule 3 it stays hand-authored but carries a
// rowResponse drift-guard (see ./intake-response.drift.test.ts). Keys are
// camelCase (ADR-066); the element vocabularies are reused from the
// assessments, onboarding and consent contracts rather than re-declared, so a
// band or status can never diverge between surfaces.
//
// These are RESPONSE schemas a client (Atlas) parses, so none of them is
// `.strict()`: an unknown key is stripped, never rejected, which is what lets a
// server add a field without breaking an installed bundle (ADR-135 Rule 1).
// The request-side contracts keep their strictness elsewhere.
//
// Each section is independently nullable: a member may have onboarding without
// a screening, a screening without onboarding, both, or neither. "Neither" is a
// normal state (a member who has not started intake), never a 404.

/**
 * The member's onboarding session. `flowId` reports WHICH flow produced these
 * answers — the standard intake flow or the CAB (Sona-handoff) flow — because
 * `onboarding_sessions` is unique per (member, flow) and a demo-minted member
 * holds only the CAB row. Without it a reader cannot tell whether an absent
 * answer means "not asked by this flow version" or "not answered".
 *
 * `answers` is returned verbatim — it is flow-version-dependent jsonb, so the
 * server never reshapes it. The one thing the server does READ out of it is the
 * reported program (via the shared `resolvePrimaryProgramKey`), so that program
 * vocabulary is resolved server-side per ADR-015 instead of leaving a client to map
 * a code to copy.
 */
export const intakeOnboardingSectionSchema = z.object({
  flowId: z.string(),
  status: onboardingStatusSchema,
  answers: onboardingAnswersSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type IntakeOnboardingSection = z.infer<typeof intakeOnboardingSectionSchema>;

/**
 * One earlier completed screening, carrying enough to read a direction of
 * travel (date, band, scores) and deliberately NOT item-level answers — the
 * history is context, not a second full record.
 */
export const intakeScreeningHistoryEntrySchema = z.object({
  startedAt: z.string(),
  completedAt: z.string(),
  resultKey: assessmentResultKeySchema,
  extensions: z.array(assessmentExtensionSchema),
  phq2Subscore: z.number().int().nullable(),
  gad2Subscore: z.number().int().nullable(),
  phq9Total: z.number().int().nullable(),
  gad7Total: z.number().int().nullable(),
});
export type IntakeScreeningHistoryEntry = z.infer<typeof intakeScreeningHistoryEntrySchema>;

/**
 * The member's most recent screening session (active or completed) plus its
 * item-level answers and the bounded history of earlier completed sessions.
 *
 * Every score here is the value STORED on the session row — the same source the
 * member's own app reads — never a re-computation and never the normalized
 * `assessment_scores` table, which is written by a separate non-transactional
 * upsert and can therefore disagree with what the member was shown.
 *
 * An `active` session legitimately has `resultKey`, `completedAt`, and the
 * escalated totals all null; that is an in-progress screening, not missing data.
 */
export const intakeAssessmentSectionSchema = z.object({
  sessionId: z.string(),
  status: assessmentSessionStatusSchema,
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  extensions: z.array(assessmentExtensionSchema),
  resultKey: assessmentResultKeySchema.nullable(),
  phq2Subscore: z.number().int().nullable(),
  gad2Subscore: z.number().int().nullable(),
  phq9Total: z.number().int().nullable(),
  gad7Total: z.number().int().nullable(),
  answers: assessmentAnswerMapSchema,
  history: z.array(intakeScreeningHistoryEntrySchema),
});
export type IntakeAssessmentSection = z.infer<typeof intakeAssessmentSectionSchema>;

/**
 * Vocabulary for the program the member REPORTED at intake, resolved SERVER-side
 * so a care-team client renders whatever arrives and launching a new condition
 * needs no client change (ADR-015 Rule 2/5).
 *
 * It is the reported program and not the member's current enrollment because every
 * value in this response is an intake-time record: someone who has since switched
 * programs, or holds no active enrollment at all, must still read honestly here.
 * Their current program is the Programs card's job.
 *
 * `null` means the server looked and found nothing to resolve — either no program
 * was reported, or the reported code is absent from the catalog. A client may then
 * present the raw reported code as unrecognised, which is a claim it can only make
 * truthfully because the server already tried.
 *
 * `usageUnit` is the countable noun for the member's logged week; it is null when
 * the program's catalog config declares none, and a client MUST then render those
 * numbers unitless rather than borrow another program's unit.
 */
export const intakeProgramSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  usageUnit: z.string().nullable(),
});
export type IntakeProgram = z.infer<typeof intakeProgramSchema>;

/**
 * The anchor's read: enough to render a dated affordance and to tell "no record"
 * apart from "not loaded", and nothing else.
 *
 * It exists so the member-detail page does not pull a member's screening into the
 * browser merely because someone opened their page. The full record is the deepest
 * PHI this product holds; a summary that carries only whether it exists and when it
 * was captured lets the page stay honest about what it has fetched.
 *
 * `capturedAt` is null for a member whose only intake is a screening with no
 * onboarding session — `hasRecord` is still true, so the affordance renders
 * undated rather than reading as absent.
 */
export const intakeSummaryDataSchema = z.object({
  hasRecord: z.boolean(),
  capturedAt: z.string().nullable(),
});
export type IntakeSummaryData = z.infer<typeof intakeSummaryDataSchema>;

export const intakeSummaryResponseSchema = apiEnvelope(intakeSummaryDataSchema);
export type IntakeSummaryResponse = z.infer<typeof intakeSummaryResponseSchema>;

/**
 * The member's onboarding answers, normalised.
 *
 * A DERIVED section, a sibling of `onboarding` rather than a member of it —
 * exactly like `program`. The onboarding section is tied to `onboarding_sessions`
 * columns by a `rowResponse` drift-guard (ADR-066 Rule 3), and putting a derived
 * field inside it would force an exclusion into that guard and weaken the column
 * parity it exists to assert.
 *
 * Additive: `onboarding.answers` still carries the stored record verbatim, so a
 * consumer migrates onto this list when it is ready rather than all at once.
 *
 * `null` when the member has no onboarding session at all — distinct from `[]`,
 * which is a session holding no answers the reader resolves.
 */
export const intakeNormalisedAnswersSchema = z.array(normalisedOnboardingAnswerSchema);
export type IntakeNormalisedAnswers = z.infer<typeof intakeNormalisedAnswersSchema>;

/**
 * Every programme the member's answers select, resolved against the catalogue.
 *
 * `program` above names the single clinical-priority pick and keeps its
 * contract; this is the full selection. Both are DATA — `displayName` comes
 * from `program_definitions`, which is per-environment configuration that has
 * to be launchable without a client release (ADR-015). That is what separates
 * it from the answer wording, which is static English for one staff surface and
 * therefore stays with the surface that renders it.
 *
 * Legacy positional codes a migrated member still carries (`focus2`) are folded
 * onto their canonical key before the lookup, so a second selected programme
 * cannot come back as a raw code beside a resolved one.
 *
 * Empty when the member reported no programme; `null` when there is no
 * onboarding session at all, mirroring `normalisedAnswers`.
 */
export const intakeReportedProgramsSchema = z.array(intakeProgramSchema);
export type IntakeReportedPrograms = z.infer<typeof intakeReportedProgramsSchema>;

/**
 * The member's standing on the Sona AI consent, read from the latest `ai_sona`
 * row: `unknown` with null fields when no decision was ever recorded. Carried
 * on every record (including the all-null one) so the care team can see that
 * a member skipped Sona even before any intake answer exists.
 */
export const intakeAiSonaConsentSchema = z.object({
  status: aiConsentStatusSchema,
  documentVersion: z.string().nullable(),
  recordedAt: z.string().nullable(),
});
export type IntakeAiSonaConsent = z.infer<typeof intakeAiSonaConsentSchema>;

/**
 * The member's designated baseline for the reported programme: its canonical
 * week, plus the same-capture reported week when the designation is a typical
 * week. A DERIVED sibling section, like `program`, read from the baselines
 * model rather than out of `onboarding.answers`, so a migrated member whose
 * baseline came from Pelago 1.0 renders the same way as a native one.
 *
 * `null` when the member has no designated baseline for that programme (or
 * reported none). Optional as well as nullable because it was added after
 * clients shipped: a response from a server that predates it must still parse,
 * and the client then renders its "no baseline" state (ADR-135).
 *
 * The section's vocabularies (kind, source, unit, details) are closed enums
 * that will grow, and this section is nested inside the whole record a client
 * parses. Clients therefore parse with `intakeClientResponseSchema`, where a
 * value the bundle does not know degrades the SECTION to null and the clinical
 * record around it still renders. The route contract keeps the plain schema
 * because the OpenAPI generator cannot describe a catch.
 */
export const intakeBaselineSectionSchema = intakeBaselineSchema;
export type IntakeBaselineSection = z.infer<typeof intakeBaselineSectionSchema>;

/** `GET /api/v1/internal/intake` unwrapped body. */
export const intakeDataSchema = z.object({
  onboarding: intakeOnboardingSectionSchema.nullable(),
  assessment: intakeAssessmentSectionSchema.nullable(),
  program: intakeProgramSchema.nullable(),
  normalisedAnswers: intakeNormalisedAnswersSchema.nullable(),
  reportedPrograms: intakeReportedProgramsSchema.nullable(),
  aiSonaConsent: intakeAiSonaConsentSchema,
  baseline: intakeBaselineSectionSchema.nullable().optional(),
});
export type IntakeData = z.infer<typeof intakeDataSchema>;

export const intakeResponseSchema = apiEnvelope(intakeDataSchema);

/** The client parse of the intake body: an unreadable `baseline` section degrades to null. */
export const intakeClientDataSchema = intakeDataSchema.extend({
  baseline: intakeBaselineSectionSchema.nullable().optional().catch(null),
});
/** The client parse of `GET /api/v1/internal/intake`; see `intakeClientDataSchema`. */
export const intakeClientResponseSchema = apiEnvelope(intakeClientDataSchema);
export type IntakeResponse = z.infer<typeof intakeResponseSchema>;
